import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';

/**
 * PublicLayout
 * Layout for public pages (Home, Auth, Community)
 * Includes header with brand, navigation, and font scale control
 * Footer with legal links
 */
export default function PublicLayout() {
  const { fontScale, toggleFontScale } = useTheme();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isAuthPage =
    location.pathname.startsWith('/auth/login') || location.pathname.startsWith('/auth/register');
  const hasAnimatedBackground = isHomePage || isAuthPage;

  return (
    <div
      className={`${hasAnimatedBackground ? 'h-screen md:overflow-hidden' : 'min-h-screen'} flex flex-col ${hasAnimatedBackground ? '' : 'bg-neutral-50'}`}
    >
      {/* Header */}
      <header
        className={`${hasAnimatedBackground ? 'bg-purple-900/20 backdrop-blur-md border-white/10' : 'bg-white border-neutral-200'} border-b sticky top-0 z-40`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Brand */}
            <Link to="/" className="flex items-center space-x-3">
              <img src="/animfacile.png" alt="AnimFacile logo" className="h-10 w-auto" />
              <span
                className={`text-xl font-bold ${hasAnimatedBackground ? 'text-white' : 'text-brand-600'}`}
              >
                AnimFacile
              </span>
            </Link>

            {/* Navigation */}
            <nav className="flex items-center space-x-4">
              <Link
                to="/auth/login"
                className={`${hasAnimatedBackground ? 'text-white hover:text-white/80' : 'text-neutral-700 hover:text-brand-600'} transition-colors px-3 py-2`}
              >
                Se connecter
              </Link>

              {/* Font scale control */}
              <div
                className={`flex items-center ${hasAnimatedBackground ? 'border-white/20' : 'border-neutral-300'} border-l pl-4`}
              >
                <button
                  onClick={toggleFontScale}
                  className={`p-2 rounded transition-colors font-bold ${hasAnimatedBackground ? 'hover:bg-white/10 text-white' : 'hover:bg-neutral-100'}`}
                  aria-label={
                    fontScale === 'xl' ? 'Réduire la taille du texte' : 'Agrandir le texte'
                  }
                  title="Taille du texte"
                >
                  {fontScale === 'xl' ? 'A-' : 'A+'}
                </button>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main
        role="main"
        className={`flex-1 ${hasAnimatedBackground ? 'w-full px-0 py-0' : 'max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8'}`}
      >
        <Outlet />
      </main>

      {/* Footer */}
      <footer
        className={`${hasAnimatedBackground ? 'bg-white backdrop-blur-sm ' : 'bg-white border-t border-neutral-200'} mt-auto relative z-50`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div
            className={`flex flex-wrap items-center justify-between gap-4 text-xs sm:text-sm ${hasAnimatedBackground ? 'text-neutral' : 'text-neutral-600'}`}
          >
            <p>&copy; {new Date().getFullYear()} AnimFacile. Tous droits réservés.</p>
            <nav className="flex space-x-4 sm:space-x-6">
              <a href="#" className={`transition-colors hover:text-brand-600`}>
                Mentions légales
              </a>
              <a href="#" className={`transition-colors hover:text-brand-600`}>
                Confidentialité
              </a>
              <a href="#" className={`transition-colors hover:text-brand-600`}>
                Aide
              </a>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
